import type { Dispatch, SetStateAction } from "react";

import { IconFileCopy, IconSelectOnModel, IconSurfaceFloor, IconSurfaceLeft, IconSurfaceRight } from "@synthesis/shared-icons";
import type { ModelDrawingPlacementDtoStatusEnumKey } from "@synthesis/shared-services/models/modelsDrawingsController/types";
import type { DropdownScheme } from "@synthesis/shared-ui/Dropdown";

/** Selected area in PDF user space (points, 1/72"). Origin is bottom-left of the page. */
export type SelectionCoords = {
  originX: number;
  originY: number;
  areaWidth: number;
  areaHeight: number;
};

/** CropBox bounds in PDF user space (points). */
export type PdfCropBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Drawing plane values used by the radio group in AddToCollectionPopup. */
export type DrawingPlane = "Разрез YZ" | "Разрез XZ" | "План XY";

export type DrawingRotation = 0 | 90 | 180 | 270;
export type DrawingRotationName = "DEG_0" | "DEG_90" | "DEG_180" | "DEG_270";
export type DrawingRotationInput = DrawingRotation | DrawingRotationName | null | undefined;

const DRAWING_ROTATION_BY_NAME: Record<string, DrawingRotation> = {
  DEG_0: 0,
  DEG_90: 90,
  DEG_180: 180,
  DEG_270: 270,
};

const DRAWING_ROTATION_TO_NAME: Record<DrawingRotation, DrawingRotationName> = {
  0: "DEG_0",
  90: "DEG_90",
  180: "DEG_180",
  270: "DEG_270",
};

export const normalizeDrawingRotation = (rotation: DrawingRotationInput): DrawingRotation => {
  if (rotation === 0 || rotation === 90 || rotation === 180 || rotation === 270) {
    return rotation;
  }

  if (typeof rotation === "string") {
    return DRAWING_ROTATION_BY_NAME[rotation.toUpperCase()] ?? 0;
  }

  return 0;
};

export const toDrawingRotationName = (rotation: DrawingRotationInput): DrawingRotationName =>
  DRAWING_ROTATION_TO_NAME[normalizeDrawingRotation(rotation)];

export type ModalOpenPDFProps = {
  fileId?: string;
  fileName: string;
  pageCount?: number;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  openModal: () => void;
  closeModal: () => void;
  /** Project id for drawing-collection create/update. Not required for view/download. */
  projectId?: string;
  mode?: "default" | "define";
  /** PDF page to open in define mode (1-based). */
  initialPage?: number;
  /** Saved highlight area in PDF points (from drawing response). */
  initialSelection?: SelectionCoords | null;
  /** Drawing id for highlight-area updates in define mode. */
  drawingId?: string;
  /** Saved drawing name to pre-fill the popup in define mode. */
  initialName?: string;
  /** Saved drawing plane to pre-select the radio in define mode. */
  initialPlane?: DrawingPlane;
  /** Saved drawing page rotation. */
  initialRotation?: DrawingRotationInput;
};

export type ContentModalProps = {
  fileName: string;
  fileId?: string;
  isOpen: boolean;
  pageCount?: number;
  projectId?: string;
  mode?: "default" | "define";
  initialPage?: number;
  initialSelection?: SelectionCoords | null;
  drawingId?: string;
  initialName?: string;
  initialPlane?: DrawingPlane;
  initialRotation?: DrawingRotationInput;
};

export type ViewerState = {
  scale: number;
  rotation: DrawingRotation;
  isLeftPanelHidden: boolean;
  panX: number;
  panY: number;
};

export type SelectionState = {
  rect: { x: number; y: number; w: number; h: number } | null;
  result: SelectionCoords | null;
  isModalOpen: boolean;
  isActive: boolean;
};

export type ResizeHandle = "nw" | "ne" | "sw" | "se";
export type DownloadKind = "file" | "page" | "area";
export type Size = { width: number; height: number };
export type Point = { x: number; y: number };

export type PanSession = {
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
  isMinimap: boolean;
};

export type DragSession = {
  startX: number;
  startY: number;
  rectX: number;
  rectY: number;
};

export type ResizeSession = {
  startX: number;
  startY: number;
  rectX: number;
  rectY: number;
  rectW: number;
  rectH: number;
  handle: ResizeHandle;
};

export type ViewportMetrics = {
  containerWidth: number;
  containerHeight: number;
  naturalWidth: number;
  naturalHeight: number;
  rotation: number;
};

/** Raster preview DPI used by the PDF page image viewer. */
export const PDF_PAGE_DPI = 300;
export const DEFAULT_SCALE = 1.25;
export const ZOOM_STEP = 0.25;
export const MAX_SCALE = 5;
export const MIN_SCALE = 0.25;
export const ROTATION_STEP_DEG = 90;
export const DEGREES_IN_CIRCLE = 360;
export const VIEWPORT_EPSILON = 0.01;
export const HANDLE_SIZE = 10;
export const HANDLE_HALF_SIZE = HANDLE_SIZE / 2;
export const MIN_RESIZE_SIZE = 4;
export const MIN_SELECTION_SIZE = 2;
export const SIDEBAR_WIDTH_PX = 180;
export const PREVIEW_WIDTH_PX = 134;
export const PREVIEW_HEIGHT_PX = 106;
export const PAGE_BADGE_SIZE_PX = 18;
export const MODAL_MIN_HEIGHT_PX = 400;
export const MODAL_MAX_WIDTH_PX = 1400;
export const MODAL_SIZE_PERCENT = 90;
export const SCALE_PERCENT = 100;
export const UNROTATED_ANGLE = 0;
export const COPY_PAGE_NAME_DATETIME_FORMAT = "DD.MM.YYYY_HH:mm";
export const MINIMAP_WIDTH = 134;
export const MINIMAP_HEIGHT = 106;

/** Placement statuses that lock the binding — changing the area or plane requires repositioning. */
export const PROTECTED_PLACEMENT_STATUSES: ModelDrawingPlacementDtoStatusEnumKey[] = ["READY_FOR_PLACEMENT", "PLACED"];

export const DEFAULT_VIEWER: ViewerState = {
  scale: DEFAULT_SCALE,
  rotation: UNROTATED_ANGLE,
  isLeftPanelHidden: false,
  panX: 0,
  panY: 0,
};

export const EMPTY_SELECTION: SelectionState = { rect: null, result: null, isModalOpen: false, isActive: false };

export const INITIAL_MINIMAP_VIEWPORT = {
  left: 0,
  top: 0,
  width: MINIMAP_WIDTH,
  height: MINIMAP_HEIGHT,
};

export const COLLECTION_ACTION = {
  SELECT_AREA: "selectArea",
  COPY_PAGE: "copyPage",
} as const;

export type CollectionAction = (typeof COLLECTION_ACTION)[keyof typeof COLLECTION_ACTION] | "";

export const COLLECTION_SCHEME = [
  {
    type: "ITEMS" as const,
    elements: [
      {
        id: COLLECTION_ACTION.SELECT_AREA,
        label: "Задать область чертежа",
        value: COLLECTION_ACTION.SELECT_AREA,
        icon: <IconSelectOnModel className="size-5" />,
      },
      {
        id: COLLECTION_ACTION.COPY_PAGE,
        label: "Копировать всю страницу",
        value: COLLECTION_ACTION.COPY_PAGE,
        icon: <IconFileCopy className="size-5" />,
      },
    ],
  },
] satisfies DropdownScheme;

export const COLLECTION_ACTIVE_ITEM_LABELS: Record<string, string> = {
  [COLLECTION_ACTION.SELECT_AREA]: "Задать область чертежа",
  [COLLECTION_ACTION.COPY_PAGE]: "Копировать всю страницу",
};

export type PagePreview = {
  page: number;
  img?: string;
};

export { getPreviewImageSrc, mapPdfPreviewsResponse } from "../utils/pdf-preview";

export const renderRadioItems = [
  {
    label: "Разрез YZ",
    value: "Разрез YZ",
    id: "Разрез YZ",
    icon: <IconSurfaceLeft className="text-brand-40 size-5" />,
  },
  {
    label: "Разрез XZ",
    value: "Разрез XZ",
    id: "Разрез XZ",
    icon: <IconSurfaceRight className="text-brand-40 size-5" />,
  },
  {
    label: "План XY",
    value: "План XY",
    id: "План XY",
    icon: <IconSurfaceFloor className="text-brand-40 size-5" />,
  },
];

export type ContentRect = {
  x: number;
  y: number;
  w: number;
  h: number;
};

/** Full stage box in stage-local coordinates (origin top-left of the PDF image stage). */
export const getStageContentRect = (stageWidth: number, stageHeight: number): ContentRect => {
  return { x: 0, y: 0, w: stageWidth, h: stageHeight };
};

/**
 * Convert a pointer position from viewport/client space into stage-local pixels.
 * Inverts `translate(pan) scale(scale) rotate(rotation)` with transform-origin at the stage center.
 */
export const clientToStageLocal = (
  clientX: number,
  clientY: number,
  containerLeft: number,
  containerTop: number,
  stageLeft: number,
  stageTop: number,
  stageWidth: number,
  stageHeight: number,
  panX: number,
  panY: number,
  scale: number,
  rotationDeg: number,
): { x: number; y: number } => {
  const cx = clientX - containerLeft;
  const cy = clientY - containerTop;
  const centerX = stageLeft + stageWidth / 2;
  const centerY = stageTop + stageHeight / 2;

  let dx = cx - centerX - panX;
  let dy = cy - centerY - panY;
  if (scale !== 0) {
    dx /= scale;
    dy /= scale;
  }

  const rad = (-rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: dx * cos - dy * sin + stageWidth / 2,
    y: dx * sin + dy * cos + stageHeight / 2,
  };
};

export const calcSelectionCoords = (
  rect: { x: number; y: number; w: number; h: number },
  contentRect: ContentRect,
  cropBox: PdfCropBox,
): SelectionCoords => {
  const { x: imgX, y: imgY, w: imgW, h: imgH } = contentRect;
  if (!imgW || !imgH || !cropBox.width || !cropBox.height) {
    return { originX: 0, originY: 0, areaWidth: 0, areaHeight: 0 };
  }

  const relX = Math.max(0, Math.min(1, (rect.x - imgX) / imgW));
  const relY = Math.max(0, Math.min(1, (rect.y - imgY) / imgH));
  const relW = Math.max(0, Math.min(1 - relX, rect.w / imgW));
  const relH = Math.max(0, Math.min(1 - relY, rect.h / imgH));

  // PDF user space: origin bottom-left, Y up. Image pixels: origin top-left, Y down.
  const coords: SelectionCoords = {
    originX: cropBox.x + relX * cropBox.width,
    originY: cropBox.y + (1 - relY - relH) * cropBox.height,
    areaWidth: relW * cropBox.width,
    areaHeight: relH * cropBox.height,
  };

  return coords;
};

export const calcScreenRect = (
  result: SelectionCoords,
  contentRect: ContentRect,
  cropBox: PdfCropBox,
): { x: number; y: number; w: number; h: number } => {
  if (!cropBox.width || !cropBox.height || !contentRect.w || !contentRect.h) {
    return { x: 0, y: 0, w: 0, h: 0 };
  }

  const relX = Math.max(0, Math.min(1, (result.originX - cropBox.x) / cropBox.width));
  const relW = Math.max(0, Math.min(1 - relX, result.areaWidth / cropBox.width));
  const relH = Math.max(0, Math.min(1, result.areaHeight / cropBox.height));
  const relY = Math.max(0, Math.min(1 - relH, 1 - (result.originY - cropBox.y) / cropBox.height - relH));
  return {
    x: contentRect.x + relX * contentRect.w,
    y: contentRect.y + relY * contentRect.h,
    w: relW * contentRect.w,
    h: relH * contentRect.h,
  };
};

/** Clamp selection to the transformed image content box (may extend outside the viewport when zoomed). */
export const clampRectToImageBounds = (
  rect: { x: number; y: number; w: number; h: number },
  contentRect: ContentRect,
  minSize = MIN_RESIZE_SIZE,
): { x: number; y: number; w: number; h: number } => {
  const imgX = contentRect.x;
  const imgY = contentRect.y;
  const imgW = contentRect.w;
  const imgH = contentRect.h;

  const w = Math.max(minSize, Math.min(rect.w, imgW));
  const h = Math.max(minSize, Math.min(rect.h, imgH));
  const x = Math.max(imgX, Math.min(rect.x, imgX + imgW - w));
  const y = Math.max(imgY, Math.min(rect.y, imgY + imgH - h));

  return { x, y, w, h };
};

export type MinimapImageBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type MinimapViewport = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type MinimapMetrics = {
  containerWidth: number;
  containerHeight: number;
  naturalWidth: number;
  naturalHeight: number;
  scale: number;
  panX: number;
  panY: number;
  rotation?: number;
  minimapWidth?: number;
  minimapHeight?: number;
};

const getEffectiveDimensions = (naturalWidth: number, naturalHeight: number, rotation = 0) => {
  const normalizedRotation = ((rotation % 360) + 360) % 360;
  const shouldSwapDimensions = normalizedRotation === 90 || normalizedRotation === 270;

  return {
    width: shouldSwapDimensions ? naturalHeight : naturalWidth,
    height: shouldSwapDimensions ? naturalWidth : naturalHeight,
  };
};

const isQuarterTurn = (rotation = 0) => {
  const normalizedRotation = ((rotation % 360) + 360) % 360;
  return normalizedRotation === 90 || normalizedRotation === 270;
};

const getFitSizes = (
  containerWidth: number,
  containerHeight: number,
  naturalWidth: number,
  naturalHeight: number,
  minimapWidth: number,
  minimapHeight: number,
  rotation = 0,
) => {
  const { width, height } = getEffectiveDimensions(naturalWidth, naturalHeight, rotation);
  const fitScale = Math.min(containerWidth / width, containerHeight / height);
  const fitW = width * fitScale;
  const fitH = height * fitScale;

  const minimapFitScale = Math.min(minimapWidth / width, minimapHeight / height);
  const minimapFitW = width * minimapFitScale;
  const minimapFitH = height * minimapFitScale;

  return {
    fitW,
    fitH,
    minimapFitW,
    minimapFitH,
    minimapOffsetX: (minimapWidth - minimapFitW) / 2,
    minimapOffsetY: (minimapHeight - minimapFitH) / 2,
  };
};

const getNaturalContentBounds = (
  naturalWidth: number,
  naturalHeight: number,
  boxWidth: number,
  boxHeight: number,
  boxOffsetX: number,
  boxOffsetY: number,
): MinimapImageBounds => {
  const contentScale = Math.min(boxWidth / naturalWidth, boxHeight / naturalHeight);
  const width = naturalWidth * contentScale;
  const height = naturalHeight * contentScale;

  return {
    left: boxOffsetX + (boxWidth - width) / 2,
    top: boxOffsetY + (boxHeight - height) / 2,
    width,
    height,
  };
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const rotatePanNorm = (panNormX: number, panNormY: number, rotation = 0) => {
  const radians = (-rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  return {
    x: panNormX * cos - panNormY * sin,
    y: panNormX * sin + panNormY * cos,
  };
};

const getMinimapViewportSize = (
  containerWidth: number,
  containerHeight: number,
  naturalWidth: number,
  naturalHeight: number,
  imageBounds: MinimapImageBounds,
  scale: number,
  rotation = 0,
) => {
  const { fitW, fitH } = getFitSizes(containerWidth, containerHeight, naturalWidth, naturalHeight, MINIMAP_WIDTH, MINIMAP_HEIGHT, 0);
  const viewportContainerW = isQuarterTurn(rotation) ? containerHeight : containerWidth;
  const viewportContainerH = isQuarterTurn(rotation) ? containerWidth : containerHeight;

  return {
    viewportW: Math.min(imageBounds.width, (viewportContainerW / (fitW * scale)) * imageBounds.width),
    viewportH: Math.min(imageBounds.height, (viewportContainerH / (fitH * scale)) * imageBounds.height),
  };
};

const getPanMetrics = (
  containerWidth: number,
  containerHeight: number,
  fitW: number,
  fitH: number,
  minimapFitW: number,
  minimapFitH: number,
  scale: number,
) => {
  const scaledFitW = fitW * scale;
  const scaledFitH = fitH * scale;

  const viewportW = scaledFitW <= containerWidth ? minimapFitW : (containerWidth / scaledFitW) * minimapFitW;
  const viewportH = scaledFitH <= containerHeight ? minimapFitH : (containerHeight / scaledFitH) * minimapFitH;

  const contentMaxPanX = Math.max(0, (scaledFitW - containerWidth) / 2);
  const contentMaxPanY = Math.max(0, (scaledFitH - containerHeight) / 2);

  return {
    scaledFitW,
    scaledFitH,
    viewportW,
    viewportH,
    contentMaxPanX,
    contentMaxPanY,
  };
};

export const getContentPanLimits = (
  containerWidth: number,
  containerHeight: number,
  naturalWidth: number,
  naturalHeight: number,
  scale: number,
  rotation = 0,
) => {
  if (scale <= 1 || !naturalWidth || !naturalHeight) {
    return { contentMaxPanX: 0, contentMaxPanY: 0 };
  }

  const { width, height } = getEffectiveDimensions(naturalWidth, naturalHeight, rotation);
  const fitScale = Math.min(containerWidth / width, containerHeight / height);
  const fitW = width * fitScale;
  const fitH = height * fitScale;

  const { contentMaxPanX, contentMaxPanY } = getPanMetrics(containerWidth, containerHeight, fitW, fitH, 0, 0, scale);

  return { contentMaxPanX, contentMaxPanY };
};

export type MainContentMetrics = {
  fitW: number;
  fitH: number;
};

export const getMainContentMetrics = (
  containerWidth: number,
  containerHeight: number,
  naturalWidth: number,
  naturalHeight: number,
  rotation = 0,
): MainContentMetrics => {
  const { fitW, fitH } = getFitSizes(containerWidth, containerHeight, naturalWidth, naturalHeight, MINIMAP_WIDTH, MINIMAP_HEIGHT, rotation);

  return { fitW, fitH };
};

export const calcMinimapViewport = ({
  containerWidth,
  containerHeight,
  naturalWidth,
  naturalHeight,
  scale,
  panX,
  panY,
  rotation = 0,
  minimapWidth = MINIMAP_WIDTH,
  minimapHeight = MINIMAP_HEIGHT,
}: MinimapMetrics): MinimapViewport => {
  const { fitW, fitH } = getFitSizes(containerWidth, containerHeight, naturalWidth, naturalHeight, minimapWidth, minimapHeight, rotation);

  const { minimapFitW, minimapFitH, minimapOffsetX, minimapOffsetY } = getFitSizes(
    minimapWidth,
    minimapHeight,
    naturalWidth,
    naturalHeight,
    minimapWidth,
    minimapHeight,
    0,
  );

  const imageBounds = getNaturalContentBounds(naturalWidth, naturalHeight, minimapFitW, minimapFitH, minimapOffsetX, minimapOffsetY);

  if (scale <= 1) {
    return {
      left: imageBounds.left,
      top: imageBounds.top,
      width: imageBounds.width,
      height: imageBounds.height,
    };
  }

  const { contentMaxPanX, contentMaxPanY } = getPanMetrics(containerWidth, containerHeight, fitW, fitH, imageBounds.width, imageBounds.height, scale);
  const { viewportW, viewportH } = getMinimapViewportSize(containerWidth, containerHeight, naturalWidth, naturalHeight, imageBounds, scale, rotation);

  const panNormX = contentMaxPanX > 0 ? clamp(panX / contentMaxPanX, -1, 1) : 0;
  const panNormY = contentMaxPanY > 0 ? clamp(panY / contentMaxPanY, -1, 1) : 0;
  const panNormLocal = rotatePanNorm(panNormX, panNormY, rotation);

  const centerLeft = imageBounds.left + (imageBounds.width - viewportW) / 2;
  const centerTop = imageBounds.top + (imageBounds.height - viewportH) / 2;
  const travelX = centerLeft - imageBounds.left;
  const travelY = centerTop - imageBounds.top;

  const left = centerLeft - panNormLocal.x * travelX;
  const top = centerTop - panNormLocal.y * travelY;

  return {
    left: Math.max(imageBounds.left, Math.min(imageBounds.left + imageBounds.width - viewportW, left)),
    top: Math.max(imageBounds.top, Math.min(imageBounds.top + imageBounds.height - viewportH, top)),
    width: viewportW,
    height: viewportH,
  };
};

export const calcMinimapPanDelta = ({
  dx,
  dy,
  containerWidth,
  containerHeight,
  naturalWidth,
  naturalHeight,
  scale,
  rotation = 0,
  minimapWidth = MINIMAP_WIDTH,
  minimapHeight = MINIMAP_HEIGHT,
}: Omit<MinimapMetrics, "panX" | "panY"> & { dx: number; dy: number }) => {
  if (scale <= 1) {
    return { deltaX: 0, deltaY: 0 };
  }

  const { fitW, fitH } = getFitSizes(containerWidth, containerHeight, naturalWidth, naturalHeight, minimapWidth, minimapHeight, rotation);

  const { minimapFitW, minimapFitH, minimapOffsetX, minimapOffsetY } = getFitSizes(
    minimapWidth,
    minimapHeight,
    naturalWidth,
    naturalHeight,
    minimapWidth,
    minimapHeight,
    0,
  );

  const imageBounds = getNaturalContentBounds(naturalWidth, naturalHeight, minimapFitW, minimapFitH, minimapOffsetX, minimapOffsetY);

  const { contentMaxPanX, contentMaxPanY } = getPanMetrics(containerWidth, containerHeight, fitW, fitH, imageBounds.width, imageBounds.height, scale);
  const { viewportW, viewportH } = getMinimapViewportSize(containerWidth, containerHeight, naturalWidth, naturalHeight, imageBounds, scale, rotation);

  const travelX = (imageBounds.width - viewportW) / 2;
  const travelY = (imageBounds.height - viewportH) / 2;

  return {
    deltaX: travelX > 0 && contentMaxPanX > 0 ? (-dx * contentMaxPanX) / travelX : 0,
    deltaY: travelY > 0 && contentMaxPanY > 0 ? (-dy * contentMaxPanY) / travelY : 0,
  };
};

export const getStageLayout = (containerWidth: number, containerHeight: number, naturalWidth: number, naturalHeight: number) => {
  if (!naturalWidth || !naturalHeight || !containerWidth || !containerHeight) {
    return { fitW: 0, fitH: 0, left: 0, top: 0 };
  }

  // Stage follows the unrotated page aspect; CSS rotate is applied on the stage.
  const { fitW, fitH } = getMainContentMetrics(containerWidth, containerHeight, naturalWidth, naturalHeight, UNROTATED_ANGLE);
  return {
    fitW,
    fitH,
    left: (containerWidth - fitW) / 2,
    top: (containerHeight - fitH) / 2,
  };
};

export const isSameViewport = (prev: MinimapViewport, next: MinimapViewport, epsilon = VIEWPORT_EPSILON) => {
  return (
    Math.abs(prev.left - next.left) < epsilon &&
    Math.abs(prev.top - next.top) < epsilon &&
    Math.abs(prev.width - next.width) < epsilon &&
    Math.abs(prev.height - next.height) < epsilon
  );
};

export const isSameSize = (prev: Size, next: Size, epsilon = VIEWPORT_EPSILON) => {
  return Math.abs(prev.width - next.width) < epsilon && Math.abs(prev.height - next.height) < epsilon;
};

export const isSameRect = (
  prev: { x: number; y: number; w: number; h: number } | null | undefined,
  next: { x: number; y: number; w: number; h: number },
) => {
  return !!prev && prev.x === next.x && prev.y === next.y && prev.w === next.w && prev.h === next.h;
};

export const isValidSelectionCoords = (coords: SelectionCoords | null | undefined): coords is SelectionCoords => {
  return !!coords && coords.areaWidth > 0 && coords.areaHeight > 0;
};

export const buildDefaultSelectionState = (initialSelection?: SelectionCoords | null): SelectionState => {
  if (isValidSelectionCoords(initialSelection)) {
    return {
      result: { ...initialSelection },
      rect: null,
      isModalOpen: false,
      isActive: false,
    };
  }

  return EMPTY_SELECTION;
};

export const hasVisibleRect = (
  rect: { x: number; y: number; w: number; h: number } | null | undefined,
): rect is { x: number; y: number; w: number; h: number } => {
  return !!rect && rect.w > 0 && rect.h > 0;
};

export const isDrawnSelectionBigEnough = (rect: { w: number; h: number }) => {
  return rect.w > MIN_SELECTION_SIZE && rect.h > MIN_SELECTION_SIZE;
};

export const hitTestPointInRect = (px: number, py: number, rect: { x: number; y: number; w: number; h: number }) => {
  return px >= rect.x && px <= rect.x + rect.w && py >= rect.y && py <= rect.y + rect.h;
};

export const hitTestHandle = (px: number, py: number, rect: { x: number; y: number; w: number; h: number }): ResizeHandle | null => {
  const handles = [
    { key: "nw" as const, x: rect.x - HANDLE_HALF_SIZE, y: rect.y - HANDLE_HALF_SIZE },
    { key: "ne" as const, x: rect.x + rect.w - HANDLE_HALF_SIZE, y: rect.y - HANDLE_HALF_SIZE },
    { key: "sw" as const, x: rect.x - HANDLE_HALF_SIZE, y: rect.y + rect.h - HANDLE_HALF_SIZE },
    { key: "se" as const, x: rect.x + rect.w - HANDLE_HALF_SIZE, y: rect.y + rect.h - HANDLE_HALF_SIZE },
  ];

  for (const handle of handles) {
    if (px >= handle.x && px <= handle.x + HANDLE_SIZE && py >= handle.y && py <= handle.y + HANDLE_SIZE) {
      return handle.key;
    }
  }

  return null;
};

export const resizeRectByHandle = (
  rect: { x: number; y: number; w: number; h: number },
  handle: ResizeHandle,
  dx: number,
  dy: number,
  minSize = MIN_RESIZE_SIZE,
): { x: number; y: number; w: number; h: number } => {
  const { x: rectX, y: rectY, w: rectW, h: rectH } = rect;
  let nextX = rectX;
  let nextY = rectY;
  let nextW = rectW;
  let nextH = rectH;

  if (handle === "nw") {
    nextX = Math.min(rectX + dx, rectX + rectW - minSize);
    nextY = Math.min(rectY + dy, rectY + rectH - minSize);
    nextW = rectX + rectW - nextX;
    nextH = rectY + rectH - nextY;
  } else if (handle === "ne") {
    nextY = Math.min(rectY + dy, rectY + rectH - minSize);
    nextW = Math.max(rectW + dx, minSize);
    nextH = rectY + rectH - nextY;
  } else if (handle === "sw") {
    nextX = Math.min(rectX + dx, rectX + rectW - minSize);
    nextW = rectX + rectW - nextX;
    nextH = Math.max(rectH + dy, minSize);
  } else if (handle === "se") {
    nextW = Math.max(rectW + dx, minSize);
    nextH = Math.max(rectH + dy, minSize);
  }

  return { x: nextX, y: nextY, w: nextW, h: nextH };
};

export const getDrawnSelectionRect = (start: Point, current: Point): { x: number; y: number; w: number; h: number } => {
  return {
    x: Math.min(start.x, current.x),
    y: Math.min(start.y, current.y),
    w: Math.abs(current.x - start.x),
    h: Math.abs(current.y - start.y),
  };
};

export const clampPan = (nextX: number, nextY: number, maxX: number, maxY: number): Point => {
  return {
    x: Math.max(-maxX, Math.min(maxX, nextX)),
    y: Math.max(-maxY, Math.min(maxY, nextY)),
  };
};

export const zoomInScale = (scale: number) => {
  return Math.min(scale + ZOOM_STEP, MAX_SCALE);
};

export const zoomOutScale = (scale: number) => {
  return Math.max(scale - ZOOM_STEP, MIN_SCALE);
};

export const rotateClockwise = (rotation: DrawingRotation): DrawingRotation => {
  const next = (rotation + ROTATION_STEP_DEG) % DEGREES_IN_CIRCLE;

  return next === 90 || next === 180 || next === 270 ? next : 0;
};

export const resolveViewerPageNumber = (
  mode: "default" | "define",
  initialPage: number | undefined,
  previewPage: number | undefined,
  activeIndex: number,
) => {
  if (mode === "define" && initialPage && initialPage > 0) {
    return initialPage;
  }

  return previewPage ?? activeIndex + 1;
};

export const getSelectionCursor = (isSelecting: boolean, isPanning: boolean, isDragging: boolean, resizeHandle: ResizeHandle | null) => {
  if (!isSelecting) {
    return isPanning ? "grabbing" : "grab";
  }

  if (isDragging) {
    return "move";
  }

  if (resizeHandle) {
    if (resizeHandle === "nw" || resizeHandle === "se") {
      return "nwse-resize";
    }

    return "nesw-resize";
  }

  return "crosshair";
};
